import { useState } from 'react';
import { X, Save } from 'lucide-react';

interface InspectionModalProps {
  assetId: number;
  assetName: string;
  assetType: string;
  onClose: () => void;
  onSave: (inspectionData: any) => Promise<void>;
}

export function InspectionModal({ assetId, assetName, assetType, onClose, onSave }: InspectionModalProps) {
  const isTestEquipment = assetType === 'TEST_EQUIPMENT';
  const [formData, setFormData] = useState({
    status: 'PASS',
    notes: '',
    damageDescription: '',
    severity: 'minor',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const inspectionData = {
        assetId,
        status: formData.status,
        notes: formData.notes || '',
        damageDescription: formData.status !== 'PASS' ? formData.damageDescription : null,
        severity: formData.status !== 'PASS' ? formData.severity : null,
      };

      await onSave(inspectionData);
      onClose();
    } catch (err: any) {
      const serverErrors = err.response?.data?.errors;
      const detail = Array.isArray(serverErrors) && serverErrors.length
        ? serverErrors.map((e: any) => `${e.field ? e.field + ': ' : ''}${e.message}`).join('; ')
        : null;
      setError(detail || err.response?.data?.message || 'Failed to save inspection');
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isTestEquipment ? 'Calibration Check' : 'Asset Inspection'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {assetName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {isTestEquipment ? 'Calibration Status' : 'Inspection Status'} <span className="text-red-500">*</span>
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="PASS">✅ Pass - Good Condition</option>
                <option value="FAIL">❌ Fail - Out of Service</option>
                <option value="NEEDS_REPAIR">⚠️ Needs Repair/Maintenance</option>
              </select>
            </div>

            {/* If Failed or Needs Repair */}
            {formData.status !== 'PASS' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Issue Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="damageDescription"
                    value={formData.damageDescription}
                    onChange={handleChange}
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describe the issue, damage, or why it failed..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Severity <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="severity"
                    value={formData.severity}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="minor">Minor - Can be used with caution</option>
                    <option value="major">Major - Needs immediate attention</option>
                    <option value="critical">Critical - Unsafe, remove from service</option>
                  </select>
                </div>
              </>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Inspection Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Any observations, wear patterns, or recommendations..."
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">
                {isTestEquipment ? '📋 Calibration Checklist:' : '🔍 Inspection Checklist:'}
              </h4>
              <ul className="text-sm text-blue-800 space-y-1">
                {isTestEquipment ? (
                  <>
                    <li>• Verify calibration certificate date</li>
                    <li>• Check accuracy against known standard</li>
                    <li>• Test all measurement ranges</li>
                    <li>• Document calibration date & next due</li>
                  </>
                ) : (
                  <>
                    <li>• Check for wear, cuts, or fraying</li>
                    <li>• Inspect all connection points</li>
                    <li>• Test mechanical functions</li>
                    <li>• Look for corrosion or damage</li>
                    <li>• Verify readable labels/markings</li>
                  </>
                )}
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save {isTestEquipment ? 'Calibration' : 'Inspection'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

